import React from 'react';
import { Input, Select, Tooltip } from '@shared/components';
import { useDraftValue } from '@shared/hooks/useDraftValue';
import { InfoIcon } from 'lucide-react';
import type { ConfigSetting } from '@/features/products/types/products.types';
import { vatFractionFromPercent } from '@/shared/utils/vatRate';
import { deriveFromRate } from '../../utils/vat';
import { autoIncludeLinkOptions, isAutoIncludeLinkSelectable } from '../../utils/autoIncludeLinkOptions';
import { QUOTE_BEHAVIOUR_OPTIONS, toQuoteBehaviour, fromQuoteBehaviour, type QuoteBehaviour } from '../../utils/quoteBehaviour';

const PERIOD_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'None' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
];

// Column header with a hover tooltip explaining what the figure means.
export const HelpHeader: React.FC<{ label: string; help: string }> = ({ label, help }) => (
    <span className="inline-flex items-center gap-1">
        {label}
        <Tooltip content={help}>
            <InfoIcon size={14} className="text-mp-muted" />
        </Tooltip>
    </span>
);

interface DraftInputProps {
    id: string;
    label: string;
    draftRef: React.MutableRefObject<string>;
    type?: string;
    min?: string;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    // Set on the first field only: activating Edit unmounts the focused Edit button,
    // so focus must land on the editor rather than the document body.
    autoFocus?: boolean;
}

// Module-level components: their type identity never changes across renders of
// the owning table, so typing here re-renders only the cell and never remounts it.
export const DraftInput: React.FC<DraftInputProps> = ({
    id,
    label,
    draftRef,
    type,
    min,
    className,
    placeholder,
    disabled,
    autoFocus,
}) => {
    const [value, setValue] = useDraftValue(draftRef);
    return (
        <Input
            id={id}
            value={value}
            aria-label={label}
            autoFocus={autoFocus}
            onChange={(e) => setValue(e.target.value)}
            type={type}
            min={min}
            className={className}
            placeholder={placeholder}
            disabled={disabled}
        />
    );
};

export const DraftPeriodSelect: React.FC<{
    draftRef: React.MutableRefObject<string>;
    disabled?: boolean;
}> = ({ draftRef, disabled }) => {
    const [value, setValue] = useDraftValue(draftRef);
    return (
        <Select
            id="frequency-period"
            size="md"
            options={PERIOD_OPTIONS}
            value={value}
            onChange={setValue}
            placeholder="None"
            ariaLabel="Frequency period"
            className="w-28"
            disabled={disabled}
        />
    );
};

interface DraftPriceOverrideWithGrossProps {
    draftRef: React.MutableRefObject<string>;
    vatRatePercent: string;
    disabled?: boolean;
    autoFocus?: boolean;
}

// Net override input plus a read-only derived gross beside it — the table is not a
// react-hook-form form, so it calls the same util useVatDerivedCost wraps.
export const DraftPriceOverrideWithGross: React.FC<DraftPriceOverrideWithGrossProps> = ({
    draftRef,
    vatRatePercent,
    disabled,
    autoFocus,
}) => {
    const [value, setValue] = useDraftValue(draftRef);
    const rate = vatFractionFromPercent(vatRatePercent ?? '0');
    const gross = value && rate !== null ? deriveFromRate(value, rate, 'grossFromNet') : '';
    return (
        <div className="flex items-center gap-1">
            <Input
                id="net-override"
                value={value}
                aria-label="Net override"
                autoFocus={autoFocus}
                onChange={(e) => setValue(e.target.value)}
                className="w-24 font-mono"
                placeholder="—"
                disabled={disabled}
            />
            <output
                htmlFor="net-override"
                aria-label="Gross override"
                aria-live="polite"
                className="text-xs text-mp-muted font-mono whitespace-nowrap"
            >
                <span aria-hidden="true">→ </span>
                {gross ? `£${gross}` : '—'}
            </output>
        </div>
    );
};

export const DraftLinkSelect: React.FC<{
    draftRef: React.MutableRefObject<string>;
    configSettings?: ConfigSetting[] | null;
    disabled?: boolean;
}> = ({ draftRef, configSettings, disabled }) => {
    const [value, setValue] = useDraftValue(draftRef);
    const options = React.useMemo(() => autoIncludeLinkOptions(configSettings), [configSettings]);

    React.useEffect(() => {
        if (!isAutoIncludeLinkSelectable(value, options)) {
            setValue('');
        }
    }, [options, value, setValue]);

    return (
        <Select
            id="auto-include-link"
            size="md"
            options={options}
            value={value}
            onChange={setValue}
            placeholder="Select link"
            ariaLabel="Auto-include link"
            className="w-40"
            disabled={disabled}
        />
    );
};

interface AutoIncludeEditCellProps {
    autoIncludeRef: React.MutableRefObject<boolean>;
    mandatoryRef: React.MutableRefObject<boolean>;
    linkRef: React.MutableRefObject<string>;
    configSettings?: ConfigSetting[] | null;
    disabled?: boolean;
}

const QUOTE_BEHAVIOUR_SELECT_OPTIONS = QUOTE_BEHAVIOUR_OPTIONS.map(({ value, label }) => ({ value, label }));

// The link select is revealed only for the two auto states, and reset when
// the behaviour drops back to optional — mirroring AddOnAssignmentForm.
export const AutoIncludeEditCell: React.FC<AutoIncludeEditCellProps> = ({
    autoIncludeRef,
    mandatoryRef,
    linkRef,
    configSettings,
    disabled,
}) => {
    const [behaviour, setBehaviour] = React.useState<QuoteBehaviour>(
        toQuoteBehaviour({ auto_include: autoIncludeRef.current, mandatory: mandatoryRef.current }),
    );

    return (
        <div className="flex flex-col gap-1">
            <Select
                id="quote-behaviour"
                size="md"
                options={QUOTE_BEHAVIOUR_SELECT_OPTIONS}
                value={behaviour}
                ariaLabel="Quote behaviour"
                className="w-36"
                disabled={disabled}
                onChange={(next) => {
                    const value = next as QuoteBehaviour;
                    const flags = fromQuoteBehaviour(value);
                    autoIncludeRef.current = flags.auto_include;
                    mandatoryRef.current = flags.mandatory;
                    setBehaviour(value);
                    if (value === 'optional') {
                        linkRef.current = '';
                    }
                }}
            />
            {behaviour !== 'optional' && (
                <DraftLinkSelect draftRef={linkRef} configSettings={configSettings} disabled={disabled} />
            )}
        </div>
    );
};
